import React, { useEffect, useMemo, useState } from "react";
import PageTitle from "../../PageTitle";
import LeaveNavigation from "./navigation/LeaveNavigation";
import LeaveMultiDatePicker from "./LeaveMultiDatePicker";
import { EXCUSE_REASON_CATEGORIES } from "./leaveConstants";
import useAxios from "../../../utilities/useAxios";
import {
	Table,
	Button,
	message,
	Tag,
	Card,
	Tabs,
	Layout,
	Modal,
	Form,
	DatePicker,
	Input,
	Select,
	Space,
	ConfigProvider,
	Descriptions,
} from "antd";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import locale from "antd/lib/locale/ko_KR";
import useIsMobile from "../../../hooks/useIsMobile";
import PageHeader from "../../common/PageHeader";

dayjs.locale("ko");

const { Content } = Layout;
const { TextArea } = Input;

/** 두 탭 테이블 가로 스크롤 기준폭 — 좁은 화면에서는 뷰포트에 맞춤 */
const MY_LEAVE_TABLE_SCROLL_X = 1300;

const REPORT_ONLY_LEAVE_TYPES = new Set(["시내출장", "생휴", "사유서"]);

const renderApprovalStamp = (label, status) => {
	const stampStyle = {
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		position: "relative",
		width: 22,
		height: 22,
		marginRight: 4,
		fontSize: 13,
		lineHeight: 1,
		verticalAlign: "middle",
	};
	const circleStyle = {
		position: "absolute",
		inset: 0,
		border: "1.5px solid #cf1322",
		borderRadius: "50%",
		pointerEvents: "none",
	};
	if (status === "approved" || status === "not_required") {
		return (
			<span key={label} style={stampStyle}>
				{label}
				<span style={circleStyle} aria-hidden />
			</span>
		);
	}
	if (status === "rejected") {
		return (
			<span key={label} style={stampStyle}>
				{label}
				<svg
					aria-hidden
					viewBox="0 0 22 22"
					style={{
						position: "absolute",
						inset: 0,
						width: "100%",
						height: "100%",
						pointerEvents: "none",
					}}>
					<line
						x1="4"
						y1="4"
						x2="18"
						y2="18"
						stroke="#cf1322"
						strokeWidth="1.5"
					/>
					<line
						x1="18"
						y1="4"
						x2="4"
						y2="18"
						stroke="#cf1322"
						strokeWidth="1.5"
					/>
				</svg>
			</span>
		);
	}
	return (
		<span key={label} style={{ marginRight: 6 }}>
			{label}·
		</span>
	);
};

const approvalStepShortLabels = (steps) => {
	const sorted = [...(steps || [])].sort(
		(a, b) =>
			(a.order ?? 0) - (b.order ?? 0) ||
			(a.id ?? 0) - (b.id ?? 0),
	);
	if (sorted.length >= 3) {
		const labels = ["팀", "국", "당"];
		return sorted.map((step, index) => ({
			step,
			label: labels[index] || String(index + 1),
		}));
	}
	if (sorted.length === 2) {
		return [
			{ step: sorted[0], label: "국" },
			{ step: sorted[1], label: "당" },
		];
	}
	if (sorted.length === 1) {
		const only = sorted[0];
		const label =
			only.approver_kind === "team_leader" ? "팀" : "당";
		return [{ step: only, label }];
	}
	return [];
};

const additionalUsersKey = (ids) =>
	JSON.stringify([...(ids || [])].map(Number).sort((a, b) => a - b));

const uniqSortedLeaveDayjs = (list) => {
	const keys = [
		...new Set(
			list.map((d) => d.startOf("day").format("YYYY-MM-DD")),
		),
	].sort();
	return keys.map((k) => dayjs(k));
};

/** 과거 구간형 사유서 한 줄(start~end)을 펼친 날짜 목록 */
const excuseDatesFromRecord = (record) => {
	if (record.start_date && record.end_date) {
		const s = dayjs(record.start_date).startOf("day");
		const e = dayjs(record.end_date).startOf("day");
		if (s.isAfter(e)) return [s];
		const out = [];
		let cur = s;
		while (cur.isBefore(e, "day") || cur.isSame(e, "day")) {
			out.push(cur);
			cur = cur.add(1, "day");
		}
		return out;
	}
	return [dayjs(record.leave_date).startOf("day")];
};

/** 동시 제출로 묶인 사유서 행 (신청 시각·세부분류·사유·추가 해당인 기준) */
const buildExcuseEditCluster = (record, excuseRows) => {
	const reason = (record.reason || "").trim();
	const category = record.reason_category || "";
	const auKey = additionalUsersKey(record.additional_users);
	const t0 = dayjs(record.created_at).valueOf();
	const WINDOW_MS = 20000;
	const matched = excuseRows.filter(
		(a) =>
			a.leave_type === "사유서" &&
			!a.is_applied &&
			!a.is_cancelled &&
			(a.reason_category || "") === category &&
			(a.reason || "").trim() === reason &&
			additionalUsersKey(a.additional_users) === auKey &&
			Math.abs(dayjs(a.created_at).valueOf() - t0) <=
				WINDOW_MS,
	);
	return matched.length ? matched : [record];
};

const computeMyLeaveTableScrollX = () => {
	if (typeof window === "undefined") {
		return MY_LEAVE_TABLE_SCROLL_X;
	}
	const pad = 48;
	return Math.min(
		MY_LEAVE_TABLE_SCROLL_X,
		Math.max(520, window.innerWidth - pad),
	);
};

const MyLeaveApplications = () => {
	const isMobile = useIsMobile();
	const [applications, setApplications] = useState([]);
	const [loading, setLoading] = useState(false);
	const [activeTab, setActiveTab] = useState("연차(반차)");
	const [tableScrollX, setTableScrollX] = useState(() =>
		computeMyLeaveTableScrollX(),
	);
	const [editOpen, setEditOpen] = useState(false);
	const [editingRecord, setEditingRecord] = useState(null);
	const [editingClusterIds, setEditingClusterIds] = useState([]);
	const [availableUsers, setAvailableUsers] = useState([]);
	const [form] = Form.useForm();
	const [cancelForm] = Form.useForm();
	const [cancelModalOpen, setCancelModalOpen] = useState(false);
	const [cancellingRecord, setCancellingRecord] = useState(null);
	const [detailOpen, setDetailOpen] = useState(false);
	const [detailRecord, setDetailRecord] = useState(null);

	useEffect(() => {
		fetchApplications();
	}, []);

	useEffect(() => {
		message.config({ top: 150 });
		return () => {
			message.config({ top: 8 });
		};
	}, []);

	useEffect(() => {
		const sync = () =>
			setTableScrollX(computeMyLeaveTableScrollX());
		sync();
		window.addEventListener("resize", sync);
		return () => window.removeEventListener("resize", sync);
	}, []);

	const openDetail = (record) => {
		setDetailRecord(record);
		setDetailOpen(true);
	};

	const closeDetail = () => {
		setDetailOpen(false);
		setDetailRecord(null);
	};

	const myLeaveTableProps = useMemo(
		() => ({
			scroll: {
				x: tableScrollX,
			},
			tableLayout: "fixed",
			sticky: { offsetHeader: 64 },
			style: {
				width: "100%",
			},
			onRow: (record) => ({
				onClick: () => openDetail(record),
				style: { cursor: "pointer" },
			}),
		}),
		[tableScrollX],
	);

	const fetchApplications = async () => {
		try {
			setLoading(true);
			const response = await useAxios.get(
				"/utils/leave/applications/?mine=1",
			);
			if (response.status === 200) {
				setApplications(
					[...response.data].sort(
						(a, b) =>
							dayjs(
								b.application_date,
							).valueOf() -
							dayjs(
								a.application_date,
							).valueOf(),
					),
				);
			}
		} catch (error) {
			message.error("신청 목록을 불러오는데 실패했습니다.");
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	const fetchAvailableUsers = async () => {
		try {
			const response = await useAxios.get(
				"/users/clergy-staff/",
			);
			if (response.status === 200) {
				setAvailableUsers(response.data);
			}
		} catch (error) {
			message.error("사용자 목록을 불러오는데 실패했습니다.");
			console.error(error);
		}
	};

	const openEdit = (record) => {
		setEditingRecord(record);
		if (record.leave_type === "사유서") {
			const cluster = buildExcuseEditCluster(
				record,
				excuseApplications,
			);
			setEditingClusterIds(cluster.map((c) => c.id));
			let initialLeaveDates;
			if (cluster.length > 1) {
				initialLeaveDates = uniqSortedLeaveDayjs(
					cluster.flatMap((c) =>
						excuseDatesFromRecord(c),
					),
				);
			} else {
				initialLeaveDates =
					excuseDatesFromRecord(record);
			}
			form.setFieldsValue({
				leave_dates: initialLeaveDates,
				reason: record.reason || "",
				reason_category: record.reason_category || undefined,
				additional_users: (
					record.additional_users || []
				).map((id) => id),
			});
			fetchAvailableUsers();
		} else {
			setEditingClusterIds([]);
			form.setFieldsValue({
				leave_date: dayjs(record.leave_date),
				reason: record.reason || "",
				reason_category: undefined,
				additional_users: (
					record.additional_users || []
				).map((id) => id),
				start_date: undefined,
				end_date: undefined,
			});
		}
		setEditOpen(true);
	};

	const handleEditSubmit = async (values) => {
		if (!editingRecord) return;
		try {
			setLoading(true);
			const payload = {};
			if (editingRecord.leave_type === "사유서") {
				const dates = (
					values.leave_dates || []
				).slice();
				if (!dates.length) {
					message.error(
						"사유서 대상 날짜를 하나 이상 선택해주세요.",
					);
					return;
				}
				payload.leave_dates = dates
					.sort(
						(a, b) =>
							a.valueOf() -
							b.valueOf(),
					)
					.map((d) => d.format("YYYY-MM-DD"));
				payload.reason = values.reason || "";
				payload.reason_category = values.reason_category;
				payload.additional_users =
					values.additional_users || [];
				payload.cluster_application_ids =
					editingClusterIds.length > 0
						? editingClusterIds
						: [editingRecord.id];
			} else {
				payload.leave_date =
					values.leave_date.format("YYYY-MM-DD");
			}
			const response = await useAxios.put(
				`/utils/leave/applications/${editingRecord.id}/`,
				payload,
			);
			if (response.status === 200) {
				message.success("수정되었습니다.");
				setEditOpen(false);
				setEditingRecord(null);
				setEditingClusterIds([]);
				form.resetFields();
				fetchApplications();
			}
		} catch (error) {
			const errMsg =
				error.response?.data?.error ||
				(error.response?.data &&
					JSON.stringify(error.response.data)) ||
				"수정 중 오류가 발생했습니다.";
			message.error(
				typeof errMsg === "string"
					? errMsg
					: "수정 중 오류가 발생했습니다.",
			);
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	const openCancelModal = (record) => {
		setCancellingRecord(record);
		cancelForm.resetFields();
		setCancelModalOpen(true);
	};

	const submitCancelWithReason = async () => {
		try {
			const values = await cancelForm.validateFields();
			if (!cancellingRecord) return;
			setLoading(true);
			const response = await useAxios.post(
				`/utils/leave/applications/${cancellingRecord.id}/cancel/`,
				{
					cancellation_reason:
						values.cancellation_reason.trim(),
				},
			);
			if (response.status === 200) {
				message.success("신청이 취소되었습니다.");
				setCancelModalOpen(false);
				setCancellingRecord(null);
				cancelForm.resetFields();
				fetchApplications();
			}
		} catch (error) {
			if (error?.errorFields) {
				return Promise.reject();
			}
			message.error(
				error.response?.data?.error ||
					"취소 처리 중 오류가 발생했습니다.",
			);
			console.error(error);
			return Promise.reject();
		} finally {
			setLoading(false);
		}
	};

	const annualHalfApplications = applications.filter((a) =>
		["연차", "오전반차", "오후반차", "월차"].includes(a.leave_type),
	);
	const excuseApplications = applications.filter(
		(a) => a.leave_type === "사유서",
	);
	const tripApplications = applications.filter((a) =>
		["출장", "시내출장"].includes(a.leave_type),
	);
	const familyApplications = applications.filter(
		(a) => a.leave_type === "경조사",
	);
	const sickApplications = applications.filter(
		(a) => a.leave_type === "병가",
	);
	const menstrualApplications = applications.filter(
		(a) => a.leave_type === "생휴",
	);

	const typeCol = {
		title: "구분",
		dataIndex: "leave_type",
		key: "leave_type",
		width: 86,
		render: (type) => {
			const colorMap = {
				월차: "blue",
				생휴: "pink",
				사유서: "orange",
			};
			return (
				<Tag color={colorMap[type] || "default"}>
					{type}
				</Tag>
			);
		},
	};
	const approvalCol = {
		title: "결재",
		key: "approval_status",
		width: 160,
		render: (_, record) => {
			if (REPORT_ONLY_LEAVE_TYPES.has(record.leave_type)) {
				return null;
			}
			const steps = record.approval_steps || [];
			if (steps.length) {
				return (
					<span>
						{approvalStepShortLabels(steps).map(
							({ step, label }) =>
								renderApprovalStamp(
									label,
									step.status,
								),
						)}
					</span>
				);
			}
			return (
				<span>
					{renderApprovalStamp(
						"팀",
						record.team_leader_status,
					)}
					{renderApprovalStamp(
						"국",
						record.office_director_status,
					)}
					{renderApprovalStamp(
						"당",
						record.church_leader_status,
					)}
				</span>
			);
		},
	};

	const appliedCol = {
		title: "관리자 적용",
		dataIndex: "is_applied",
		key: "is_applied",
		width: 112,
		render: (applied, record) => {
			if (record.is_cancelled) {
				return <Tag color="default">—</Tag>;
			}
			return applied ? (
				<Tag color="success">적용됨</Tag>
			) : (
				<Tag color="warning">미적용</Tag>
			);
		},
	};
	const cancelReasonCol = {
		title: "취소 사유",
		dataIndex: "cancellation_reason",
		key: "cancellation_reason",
		width: 160,
		ellipsis: true,
		render: (v, record) => (record.is_cancelled ? v || "—" : "—"),
	};
	const actionsCol = {
		title: "작업",
		key: "actions",
		width: 188,
		render: (_, record) => {
			const canEdit =
				!record.is_applied && !record.is_cancelled;
			const canCancel =
				!record.is_cancelled &&
				(!record.is_applied ||
					record.leave_type === "시내출장");
			return (
				<Space>
					{canEdit && (
						<Button
							type="primary"
							size="small"
							onClick={(e) => {
								e.stopPropagation();
								openEdit(record);
							}}>
							수정
						</Button>
					)}
					{canCancel && (
						<Button
							size="small"
							danger
							onClick={(e) => {
								e.stopPropagation();
								openCancelModal(record);
							}}>
							신청 취소
						</Button>
					)}
					{record.is_cancelled && (
						<Tag>취소됨</Tag>
					)}
				</Space>
			);
		},
	};

	const formatLeavePeriod = (record) => {
		if (!record) return "—";
		if (record.start_date && record.end_date) {
			const s = dayjs(record.start_date);
			const e = dayjs(record.end_date);
			if (s.isSame(e, "day")) {
				return s.format("YYYY-MM-DD");
			}
			return `${s.format("YYYY-MM-DD")} ~ ${e.format("YYYY-MM-DD")}`;
		}
		return dayjs(record.leave_date).format("YYYY-MM-DD");
	};

	const formatAdditionalUsers = (record) => {
		const info = record?.additional_users_info || [];
		if (!info.length) return "—";
		return info.map((u) => u.name).join(", ");
	};

	const renderDetailApproval = (record) => {
		if (!record) return "—";
		if (REPORT_ONLY_LEAVE_TYPES.has(record.leave_type)) {
			return "해당 없음";
		}
		const steps = record.approval_steps || [];
		if (steps.length) {
			return (
				<span>
					{approvalStepShortLabels(steps).map(
						({ step, label }) =>
							renderApprovalStamp(
								label,
								step.status,
							),
					)}
				</span>
			);
		}
		return (
			<span>
				{renderApprovalStamp(
					"팀",
					record.team_leader_status,
				)}
				{renderApprovalStamp(
					"국",
					record.office_director_status,
				)}
				{renderApprovalStamp(
					"당",
					record.church_leader_status,
				)}
			</span>
		);
	};
	const excusePeriodCol = {
		title: "기간",
		key: "excuse_period",
		width: 212,
		ellipsis: true,
		render: (_, record) => {
			if (record.start_date && record.end_date) {
				const s = dayjs(record.start_date);
				const e = dayjs(record.end_date);
				if (s.isSame(e, "day")) {
					return s.format("YYYY-MM-DD");
				}
				return `${s.format("YYYY-MM-DD")} ~ ${e.format("YYYY-MM-DD")}`;
			}
			return dayjs(record.leave_date).format("YYYY-MM-DD");
		},
	};
	const dateCols = [
		{
			title: "신청일",
			dataIndex: "application_date",
			key: "application_date",
			width: 120,
			render: (d) => dayjs(d).format("YYYY-MM-DD"),
		},
		{
			title: "사용일",
			key: "leave_period",
			width: 212,
			render: (_, record) => {
				if (record.start_date && record.end_date) {
					const s = dayjs(record.start_date);
					const e = dayjs(record.end_date);
					if (s.isSame(e, "day")) {
						return s.format("YYYY-MM-DD");
					}
					return `${s.format("YYYY-MM-DD")} ~ ${e.format("YYYY-MM-DD")}`;
				}
				return dayjs(record.leave_date).format("YYYY-MM-DD");
			},
		},
	];
	const excuseCols = [
		{
			title: "세부분류",
			dataIndex: "reason_category",
			key: "reason_category",
			width: 110,
			render: (t) => t || "—",
		},
		{
			title: "사유",
			dataIndex: "reason",
			key: "reason",
			width: 148,
			ellipsis: true,
			render: (t) => t || "—",
		},
		{
			title: "추가 해당인",
			key: "additional_users_info",
			width: 140,
			ellipsis: true,
			render: (_, record) => {
				const info = record.additional_users_info || [];
				if (!info.length) return "—";
				return info.map((u) => u.name).join(", ");
			},
		},
	];

	const monthlyColumns = [
		typeCol,
		...dateCols,
		approvalCol,
		appliedCol,
		cancelReasonCol,
		actionsCol,
	];
	const excuseColumns = [
		typeCol,
		dateCols[0],
		excusePeriodCol,
		...excuseCols,
		appliedCol,
		cancelReasonCol,
		actionsCol,
	];

	const tabItems = [
		{
			key: "연차(반차)",
			label: "연차(반차)",
			children: (
				<Table
					{...myLeaveTableProps}
					columns={monthlyColumns}
					dataSource={annualHalfApplications}
					rowKey="id"
					loading={loading}
					pagination={{
						pageSize: 10,
						showTotal: (t) => `총 ${t}건`,
					}}
				/>
			),
		},
		{
			key: "사유서",
			label: "사유서",
			children: (
				<Table
					{...myLeaveTableProps}
					columns={excuseColumns}
					dataSource={excuseApplications}
					rowKey="id"
					loading={loading}
					pagination={{
						pageSize: 10,
						showTotal: (t) => `총 ${t}건`,
					}}
				/>
			),
		},
		{
			key: "출장",
			label: "출장",
			children: (
				<Table
					{...myLeaveTableProps}
					columns={monthlyColumns}
					dataSource={tripApplications}
					rowKey="id"
					loading={loading}
					pagination={{
						pageSize: 10,
						showTotal: (t) => `총 ${t}건`,
					}}
				/>
			),
		},
		{
			key: "경조휴가",
			label: "경조휴가",
			children: (
				<Table
					{...myLeaveTableProps}
					columns={monthlyColumns}
					dataSource={familyApplications}
					rowKey="id"
					loading={loading}
					pagination={{
						pageSize: 10,
						showTotal: (t) => `총 ${t}건`,
					}}
				/>
			),
		},
		{
			key: "병가",
			label: "병가",
			children: (
				<Table
					{...myLeaveTableProps}
					columns={monthlyColumns}
					dataSource={sickApplications}
					rowKey="id"
					loading={loading}
					pagination={{
						pageSize: 10,
						showTotal: (t) => `총 ${t}건`,
					}}
				/>
			),
		},
		{
			key: "생리휴가",
			label: "생리휴가",
			children: (
				<Table
					{...myLeaveTableProps}
					columns={monthlyColumns}
					dataSource={menstrualApplications}
					rowKey="id"
					loading={loading}
					pagination={{
						pageSize: 10,
						showTotal: (t) => `총 ${t}건`,
					}}
				/>
			),
		},
	];

	return (
		<ConfigProvider locale={locale}>
			<PageTitle title="내 신청 내역" />
			<Layout className="member-layout">
				<LeaveNavigation />
				<Layout className="member-content-layout">
					<Content className="member-content utils-leave-my-applications-layout">
						<div className="utils-rent_sanctuary__container utils-leave-my-applications-container page">
							<PageHeader title="내 신청 내역" />
							<Card className="surface-card utils-leave-my-applications-card">
								<Tabs
									className="utils-leave-my-applications-tabs"
									activeKey={
										activeTab
									}
									onChange={
										setActiveTab
									}
									items={
										tabItems
									}
								/>
							</Card>
						</div>
					</Content>
				</Layout>
			</Layout>

			<Modal
				title="신청 상세"
				open={detailOpen}
				onCancel={closeDetail}
				width={
					typeof window !== "undefined"
						? Math.min(560, window.innerWidth - 32)
						: 560
				}
				destroyOnClose
				footer={
					detailRecord
						? (() => {
								const canEdit =
									!detailRecord.is_applied &&
									!detailRecord.is_cancelled;
								const canCancel =
									!detailRecord.is_cancelled &&
									(!detailRecord.is_applied ||
										detailRecord.leave_type ===
											"시내출장");
								return (
									<Space
										wrap
										style={{
											width: "100%",
											justifyContent:
												"flex-end",
										}}>
										<Button
											onClick={
												closeDetail
											}>
											닫기
										</Button>
										{canEdit && (
											<Button
												type="primary"
												onClick={() => {
													const r =
														detailRecord;
													closeDetail();
													openEdit(
														r,
													);
												}}>
												수정
											</Button>
										)}
										{canCancel && (
											<Button
												danger
												onClick={() => {
													const r =
														detailRecord;
													closeDetail();
													openCancelModal(
														r,
													);
												}}>
												신청 취소
											</Button>
										)}
									</Space>
								);
							})()
						: null
				}>
				{detailRecord && (
					<Descriptions
						bordered
						size="small"
						column={{
							xs: 1,
							sm: 1,
							md: 2,
						}}
						labelStyle={{
							width: 110,
							whiteSpace: "nowrap",
						}}>
						<Descriptions.Item label="구분">
							{detailRecord.leave_type}
						</Descriptions.Item>
						<Descriptions.Item label="신청일">
							{dayjs(
								detailRecord.application_date,
							).format("YYYY-MM-DD")}
						</Descriptions.Item>
						<Descriptions.Item
							label="사용일"
							span={2}>
							{formatLeavePeriod(detailRecord)}
						</Descriptions.Item>
						<Descriptions.Item
							label="결재"
							span={2}>
							{renderDetailApproval(detailRecord)}
						</Descriptions.Item>
						<Descriptions.Item label="관리자 적용">
							{detailRecord.is_cancelled ? (
								"—"
							) : detailRecord.is_applied ? (
								<Tag color="success">적용됨</Tag>
							) : (
								<Tag color="warning">미적용</Tag>
							)}
						</Descriptions.Item>
						<Descriptions.Item label="취소">
							{detailRecord.is_cancelled ? (
								<Tag>취소됨</Tag>
							) : (
								"—"
							)}
						</Descriptions.Item>
						{detailRecord.is_cancelled && (
							<Descriptions.Item
								label="취소 사유"
								span={2}>
								{detailRecord.cancellation_reason ||
									"—"}
							</Descriptions.Item>
						)}
						{detailRecord.leave_type === "사유서" && (
							<>
								<Descriptions.Item label="세부분류">
									{detailRecord.reason_category ||
										"—"}
								</Descriptions.Item>
								<Descriptions.Item
									label="사유"
									span={2}>
									<span
										style={{
											whiteSpace:
												"pre-wrap",
										}}>
										{detailRecord.reason ||
											"—"}
									</span>
								</Descriptions.Item>
								<Descriptions.Item
									label="추가 해당인"
									span={2}>
									{formatAdditionalUsers(
										detailRecord,
									)}
								</Descriptions.Item>
							</>
						)}
						{detailRecord.leave_type === "시내출장" && (
							<>
								<Descriptions.Item
									label="목적"
									span={2}>
									{detailRecord.reason || "—"}
								</Descriptions.Item>
								<Descriptions.Item label="행선지">
									{detailRecord.trip_location ||
										"—"}
								</Descriptions.Item>
								<Descriptions.Item label="출발 시각">
									{detailRecord.local_trip_departure_time
										? String(
												detailRecord.local_trip_departure_time,
											).slice(0, 5)
										: "—"}
								</Descriptions.Item>
								<Descriptions.Item
									label="복귀"
									span={2}>
									{detailRecord.local_trip_return_type ||
										"—"}
									{detailRecord.local_trip_return_time
										? ` · ${String(detailRecord.local_trip_return_time).slice(0, 5)}`
										: ""}
								</Descriptions.Item>
								<Descriptions.Item
									label="동행인"
									span={2}>
									{formatAdditionalUsers(
										detailRecord,
									)}
								</Descriptions.Item>
							</>
						)}
						{detailRecord.leave_type === "출장" && (
							<>
								<Descriptions.Item
									label="제목"
									span={2}>
									{detailRecord.trip_title ||
										"—"}
								</Descriptions.Item>
								<Descriptions.Item
									label="목적"
									span={2}>
									{detailRecord.reason || "—"}
								</Descriptions.Item>
								<Descriptions.Item
									label="배경"
									span={2}>
									<span
										style={{
											whiteSpace:
												"pre-wrap",
										}}>
										{detailRecord.trip_background ||
											"—"}
									</span>
								</Descriptions.Item>
								<Descriptions.Item label="지역">
									{detailRecord.trip_location ||
										"—"}
								</Descriptions.Item>
								<Descriptions.Item label="숙박">
									{detailRecord.trip_includes_lodging
										? "포함"
										: "미포함"}
								</Descriptions.Item>
								<Descriptions.Item label="교통">
									{detailRecord.trip_transport ||
										"—"}
								</Descriptions.Item>
								<Descriptions.Item
									label="비고"
									span={2}>
									{detailRecord.trip_note ||
										"—"}
								</Descriptions.Item>
								<Descriptions.Item
									label="동행인"
									span={2}>
									{formatAdditionalUsers(
										detailRecord,
									)}
								</Descriptions.Item>
							</>
						)}
						{["경조사", "병가", "특별휴가", "기타"].includes(
							detailRecord.leave_type,
						) &&
							detailRecord.reason && (
								<Descriptions.Item
									label="사유"
									span={2}>
									<span
										style={{
											whiteSpace:
												"pre-wrap",
										}}>
										{detailRecord.reason}
									</span>
								</Descriptions.Item>
							)}
					</Descriptions>
				)}
			</Modal>

			<Modal
				title="신청 취소"
				open={cancelModalOpen}
				onCancel={() => {
					setCancelModalOpen(false);
					setCancellingRecord(null);
					cancelForm.resetFields();
				}}
				onOk={submitCancelWithReason}
				okText="취소 확정"
				cancelText="닫기"
				confirmLoading={loading}
				destroyOnClose>
				<Form form={cancelForm} layout="vertical">
					<Form.Item
						name="cancellation_reason"
						label="취소 사유"
						rules={[
							{
								required: true,
								message: "취소 사유를 입력해주세요.",
							},
						]}>
						<TextArea
							rows={4}
							placeholder="취소 사유를 입력하세요"
						/>
					</Form.Item>
				</Form>
			</Modal>

			<Modal
				title="신청 수정"
				open={editOpen}
				onCancel={() => {
					setEditOpen(false);
					setEditingRecord(null);
					setEditingClusterIds([]);
					form.resetFields();
				}}
				footer={null}
				destroyOnClose>
				<Form
					form={form}
					layout="vertical"
					onFinish={handleEditSubmit}>
					{["월차", "생휴"].includes(
						editingRecord?.leave_type,
					) && (
						<Form.Item
							name="leave_date"
							label="사용일"
							rules={[
								{
									required: true,
									message: "사용일을 선택해주세요.",
								},
							]}>
							<DatePicker
								inputReadOnly={isMobile}
								style={{
									width: "100%",
								}}
								format="YYYY-MM-DD"
							/>
						</Form.Item>
					)}
					{editingRecord?.leave_type ===
						"사유서" && (
						<>
							<Form.Item
								name="leave_dates"
								label="대상 날짜"
								rules={[
									{
										required: true,
										message: "날짜를 하나 이상 선택해주세요.",
									},
									{
										validator: (
											_,
											v,
										) =>
											Array.isArray(
												v,
											) &&
											v.length >
												0
												? Promise.resolve()
												: Promise.reject(
														new Error(
															"날짜를 하나 이상 선택해주세요.",
														),
													),
									},
								]}>
								<LeaveMultiDatePicker />
							</Form.Item>
							<Form.Item
								name="reason_category"
								label="세부분류"
								rules={[
									{
										required: true,
										message: "세부분류를 선택해주세요.",
									},
								]}>
								<Select placeholder="세부분류를 선택하세요">
									{EXCUSE_REASON_CATEGORIES.map(
										(cat) => (
											<Select.Option
												key={
													cat
												}
												value={
													cat
												}>
												{
													cat
												}
											</Select.Option>
										),
									)}
								</Select>
							</Form.Item>
							<Form.Item
								name="reason"
								label="사유"
								rules={[
									{
										required: true,
										message: "사유를 입력해주세요.",
									},
								]}>
								<TextArea
									rows={4}
									placeholder="사유"
								/>
							</Form.Item>
							<Form.Item
								name="additional_users"
								label="추가 해당인">
								<Select
									mode="multiple"
									allowClear
									placeholder="선택 (선택 사항)"
									options={availableUsers.map(
										(
											u,
										) => ({
											value: u.id,
											label: `${u.name}${u.classification_name ? ` (${u.classification_name})` : ""}`,
										}),
									)}
								/>
							</Form.Item>
						</>
					)}
					<Form.Item>
						<Button
							type="primary"
							htmlType="submit"
							loading={loading}
							block>
							저장
						</Button>
					</Form.Item>
				</Form>
			</Modal>
		</ConfigProvider>
	);
};

export default MyLeaveApplications;
